import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  take,
  takeUntil,
} from 'rxjs';
import { BdUiSingleSelectOptionGroupConfig } from './single-select-option-group.config';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { IftaLabel } from 'primeng/iftalabel';
import { Select } from 'primeng/select';
import { OverlayOptions } from 'primeng/api';
import { BdAbstractSingleSelect } from '../abstract-single-select';
import { BdAbstractGroupOption, BdAbstractOption } from '../../multi/multi-select.config';

@Component({
  selector: 'bd-ui-single-select-option-group',
  template: `
    <p-iftalabel>
      <p-select
        [inputId]="selectConfig.id"
        [data-test]="selectConfig.id"
        [formControl]="selectCtrl"
        [options]="allOptions | async"
        [class]="
          valid
            ? 'mb-4'
            : errorMessage
              ? 'ng-invalid ng-dirty'
              : 'mb-4 ng-invalid ng-dirty'
        "
        [loading]="searching"
        fluid
        variant="filled"
        [showClear]="selectConfig.resetOption"
        [filterPlaceholder]="placeholderLabel"
        [emptyFilterMessage]="noEntriesFoundLabel"
        [emptyMessage]="noEntriesFoundLabel"
        [overlayOptions]="overlayOptions"
        [group]="true"
        dataKey="dataKey"
        optionLabel="displayName"
        optionGroupLabel="displayName"
        optionGroupChildren="options"
        [filter]="showFilter"
        [filterFields]="['searchTerm']"
        filterBy="searchTerm"
      >
      </p-select>
      <label [for]="selectConfig.id">{{ selectConfig?.selectLabel }}</label>
      @if (errorMessage) {
        <small [id]="selectConfig.id + '-help'">{{ errorMessage }}</small>
      }
    </p-iftalabel>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    BdDataTestDirective,
    IftaLabel,
    Select,
  ],
})
export class BdUiSingleSelectOptionGroupComponent<
  TOptionGroup extends { options: TOption[] },
  TOption,
>
  extends BdAbstractSingleSelect<
    TOption,
    BdAbstractOption<TOption>,
    BdAbstractGroupOption<TOptionGroup, TOption>
  >
  implements OnDestroy
{
  readonly config =
    input<BdUiSingleSelectOptionGroupConfig<TOptionGroup, TOption>>();
  readonly initialSelection = input<TOption | Observable<TOption> | undefined>(
    undefined,
  );
  readonly selectOptions = input<Observable<TOptionGroup[]>>(of([]));

  readonly selection = output<TOption | undefined>();
  readonly optionsChange = output<Array<TOptionGroup>>();

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  public showFilter = false;
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private static readonly FILTER_THRESHOLD = 3;

  public selectConfig: Required<
    BdUiSingleSelectOptionGroupConfig<TOptionGroup, TOption>
  > = {
    id: 'single-select-option-group',
    selectLabel: 'Element',
    option: {
      uniqueFnc: () => '',
      sortFnc: () => 0,
      searchOptionAccessor: () => '',
      searchOptionDisplayName: () => '',
      cloneFn: (option) => JSON.parse(JSON.stringify(option)),
    },
    optionGroup: {
      uniqueFnc: () => '',
      sortFnc: () => 0,
      searchOptionAccessor: () => '',
      searchOptionDisplayName: () => '',
      cloneFn: (option) => JSON.parse(JSON.stringify(option)),
    },
    emitEventOnInitialSelection: false,
    resetOption: false,
    valid$: new ReplaySubject<{
      isValid: boolean;
      errorMessage?: string;
    }>(1),
  };

  // Event subjects for coordinating initialization
  private _configReady$ = new ReplaySubject<void>(1);
  private _initialSelection$ = new ReplaySubject<TOption | undefined>(1);
  private _optionsLoaded$ = new ReplaySubject<void>(1);

  constructor() {
    super();

    // Initialize component when config and options are ready (does not wait for initial selection)
    combineLatest([this._configReady$, this._optionsLoaded$])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.init(this.selectConfig);
        this._cdr.markForCheck();
      });

    // Apply initial selection once a non-null value arrives AND config + options are ready.
    combineLatest([
      this._configReady$,
      this._optionsLoaded$,
      this._initialSelection$.pipe(
        filter((v): v is TOption => v !== undefined && v !== null),
        take(1),
        map((initialSelection) => this.transformToWrapper(initialSelection)),
      ),
    ])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(([, , wrappedSelection]) => {
        this.setSelection(wrappedSelection, {
          emitEvent: this.selectConfig.emitEventOnInitialSelection,
        });
        this._cdr.markForCheck();
      });

    this.allOptions.pipe(takeUntil(this._onDestroy)).subscribe((groups) => {
      const totalOptions = groups.reduce(
        (sum, group) => sum + group.options.length,
        0,
      );
      this.showFilter =
        totalOptions > BdUiSingleSelectOptionGroupComponent.FILTER_THRESHOLD;
      this._cdr.markForCheck();
    });

    // React to config changes
    effect(() => {
      const config = this.config();
      if (!config) return;
      this.selectConfig = {
        id: `single-select-option-group-${config.selectLabel}`,
        emitEventOnInitialSelection: false,
        valid$: new ReplaySubject<{
          isValid: boolean;
          errorMessage?: string;
        }>(1),
        resetOption: false,
        ...config,
        option: {
          uniqueFnc: (option) => config.option.uniqueFnc(option),
          searchOptionAccessor: (option) =>
            config.option.searchOptionAccessor(option),
          searchOptionDisplayName: (option) =>
            config.option.searchOptionDisplayName
              ? config.option.searchOptionDisplayName(option)
              : config.option.searchOptionAccessor(option),
          cloneFn: (option) => JSON.parse(JSON.stringify(option)),
          sortFnc: (a, b) => config.option.sortFnc(a, b),
        },
        optionGroup: {
          uniqueFnc: (option) => config.optionGroup.uniqueFnc(option),
          searchOptionAccessor: (option) =>
            config.optionGroup.searchOptionAccessor(option),
          searchOptionDisplayName: (option) =>
            config.optionGroup.searchOptionDisplayName
              ? config.optionGroup.searchOptionDisplayName(option)
              : config.optionGroup.searchOptionAccessor(option),
          cloneFn: (option) => JSON.parse(JSON.stringify(option)),
          sortFnc: (a, b) => config.optionGroup.sortFnc(a, b),
        },
      };
      this._configReady$.next();
    });

    // React to initialSelection changes
    effect(() => {
      const value = this.initialSelection();
      if (value instanceof Observable) {
        value
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe((it) => this._initialSelection$.next(it));
      } else {
        this._initialSelection$.next(value);
      }
    });

    // React to selectOptions changes
    effect(() => {
      const options$ = this.selectOptions();
      this._loadOptions(options$);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected onSelectionChange(
    selection: BdAbstractOption<TOption> | null,
  ): void {
    if (selection) {
      this.selection.emit(selection.realValue);
    } else {
      this.selection.emit(undefined);
    }
  }

  protected transformToWrapper(option: TOption): BdAbstractOption<TOption> {
    return {
      realValue: option,
      dataKey: this.selectConfig.option.uniqueFnc(option),
      searchTerm: this.selectConfig.option.searchOptionAccessor(option),
      displayName: this.selectConfig.option.searchOptionDisplayName(option),
    };
  }

  private _loadOptions(options$: Observable<TOptionGroup[]>): void {
    this.searching = true;

    options$
      .pipe(
        map((options) =>
          options.map(
            (it) =>
              <BdAbstractGroupOption<TOptionGroup, TOption>>{
                realValue: it,
                dataKey: this.selectConfig.optionGroup.uniqueFnc(it),
                searchTerm:
                  this.selectConfig.optionGroup.searchOptionAccessor(it),
                displayName:
                  this.selectConfig.optionGroup.searchOptionDisplayName(it),
                options: it.options
                  .sort((a, b) => this.selectConfig.option.sortFnc(a, b))
                  .map((child) => this.transformToWrapper(child)),
              },
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (availableOptions) => {
          this.allOptions.next(
            availableOptions
              .slice()
              .sort((a, b) =>
                this.selectConfig.optionGroup.sortFnc(a.realValue, b.realValue),
              ),
          );
          this.searching = false;
          this.optionsChange.emit(availableOptions.map((it) => it.realValue));
          this._optionsLoaded$.next();
        },
        error: (err) => {
          console.warn(
            `Laden von ${this.selectConfig.selectLabel} nicht möglich`,
            err,
          );
          this.allOptions.next([]);
          this.searching = false;
          this.valid = false;
          this.errorMessage = 'Laden fehlgeschlagen';
          this._optionsLoaded$.next();
          this._cdr.markForCheck();
        },
      });
  }
}
